import { createServerFn } from "@tanstack/react-start";
import { and, asc, eq, isNotNull, isNull, ne, or } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import type { Author } from "~/lib/citations/identity-resolution";
import {
  DEFAULT_EXCLUSION_CODES,
  projectCitations,
  projectExclusionCodes,
} from "~/lib/db/schema";

const phaseEnum = z.enum(["title_abstract", "full_text"]);

const projectIdSchema = z.object({
  projectId: z.string(),
  phase: phaseEnum.default("title_abstract"),
});

const citationIdSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  phase: phaseEnum.default("title_abstract"),
});

const screeningDecisionSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  decision: z.enum(["included", "excluded", "maybe"]),
  exclusionCode: z.string().optional(),
  exclusionNotes: z.string().optional(),
  phase: phaseEnum.default("title_abstract"),
});

export interface ScreeningQueueCitation {
  id: string;
  screeningOrdinal: number;
  title: string;
  authors: Author[] | null;
  year: number | null;
  containerTitle: string | null;
  doi: string | null;
  pmid: string | null;
  abstract: string | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  screeningStatus: "pending" | "included" | "excluded" | "maybe";
}

export interface ScreeningReviewedHistoryItem {
  citation: ScreeningQueueCitation;
  decision: "include" | "exclude" | "maybe";
  reviewedAt: string;
  tagNames: string[];
  queuePosition: number;
}

const reviewedHistorySchema = z.object({
  projectId: z.string(),
  phase: phaseEnum.default("title_abstract"),
  limit: z.number().int().min(1).max(100).default(20),
});

/**
 * Get all pending citations for screening (not confirmed duplicates)
 */
export const $getScreeningQueue = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        eq(projectCitations.screeningStatus, "pending"),
        // Exclude confirmed duplicates
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      with: {
        canonicalReference: true,
      },
      orderBy: (citations, { asc }) => [asc(citations.screeningOrdinal)],
    });

    return citations.map((c) => ({
      id: c.id,
      screeningOrdinal: c.screeningOrdinal,
      title: c.canonicalReference.title,
      authors: c.canonicalReference.authors as Author[] | null,
      year: c.canonicalReference.year,
      containerTitle: c.canonicalReference.containerTitle,
      doi: c.canonicalReference.doi,
      pmid: c.canonicalReference.pmid,
      abstract: c.canonicalReference.abstract,
      volume: c.canonicalReference.volume,
      issue: c.canonicalReference.issue,
      pages: c.canonicalReference.pages,
      screeningStatus: c.screeningStatus as ScreeningQueueCitation["screeningStatus"],
    })) satisfies ScreeningQueueCitation[];
  });

/**
 * Get recently reviewed citations for the current user (single-screening mode).
 * This powers persistent workbench history across refreshes/sessions.
 */
export const $getScreeningReviewedHistory = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => reviewedHistorySchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        eq(projectCitations.screenedBy, user.id),
        isNotNull(projectCitations.screenedAt),
        ne(projectCitations.screeningStatus, "pending"),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      with: {
        canonicalReference: true,
      },
      orderBy: (citations, { desc }) => [desc(citations.screenedAt)],
      limit: data.limit,
    });

    return citations
      .map((c) => {
        const reviewedAt = c.screenedAt?.toISOString();
        if (!reviewedAt) return null;

        const decision =
          c.screeningStatus === "included"
            ? "include"
            : c.screeningStatus === "excluded"
              ? "exclude"
              : c.screeningStatus === "maybe"
                ? "maybe"
                : null;

        if (!decision) return null;

        return {
          citation: {
            id: c.id,
            screeningOrdinal: c.screeningOrdinal,
            title: c.canonicalReference.title,
            authors: c.canonicalReference.authors as Author[] | null,
            year: c.canonicalReference.year,
            containerTitle: c.canonicalReference.containerTitle,
            doi: c.canonicalReference.doi,
            pmid: c.canonicalReference.pmid,
            abstract: c.canonicalReference.abstract,
            volume: c.canonicalReference.volume,
            issue: c.canonicalReference.issue,
            pages: c.canonicalReference.pages,
            screeningStatus:
              c.screeningStatus as ScreeningQueueCitation["screeningStatus"],
          },
          decision,
          reviewedAt,
          tagNames: [] as string[],
          queuePosition: c.screeningOrdinal,
        } satisfies ScreeningReviewedHistoryItem;
      })
      .filter((item): item is ScreeningReviewedHistoryItem => item !== null);
  });

/**
 * Get a single citation by ID for detailed view
 */
export const $getScreeningCitation = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
      with: {
        canonicalReference: true,
      },
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    return {
      id: citation.id,
      screeningOrdinal: citation.screeningOrdinal,
      title: citation.canonicalReference.title,
      authors: citation.canonicalReference.authors as Author[] | null,
      year: citation.canonicalReference.year,
      containerTitle: citation.canonicalReference.containerTitle,
      doi: citation.canonicalReference.doi,
      pmid: citation.canonicalReference.pmid,
      abstract: citation.canonicalReference.abstract,
      volume: citation.canonicalReference.volume,
      issue: citation.canonicalReference.issue,
      pages: citation.canonicalReference.pages,
      screeningStatus:
        citation.screeningStatus as ScreeningQueueCitation["screeningStatus"],
    } satisfies ScreeningQueueCitation;
  });

/**
 * Submit a screening decision for a citation
 */
export const $submitScreeningDecision = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => screeningDecisionSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Verify citation exists and belongs to project
    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    // Update the citation with the screening decision
    await db
      .update(projectCitations)
      .set({
        screeningStatus: data.decision,
        exclusionCode: data.decision === "excluded" ? (data.exclusionCode ?? null) : null,
        exclusionReason:
          data.decision === "excluded" ? (data.exclusionNotes ?? null) : null,
        screenedBy: user.id,
        screenedAt: new Date(),
      })
      .where(eq(projectCitations.id, data.citationId));

    return { success: true };
  });

/**
 * Undo a screening decision (reset to pending)
 */
export const $undoScreeningDecision = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Verify citation exists and belongs to project
    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    // Reset the citation to pending
    await db
      .update(projectCitations)
      .set({
        screeningStatus: "pending",
        exclusionReason: null,
        screenedBy: null,
        screenedAt: null,
      })
      .where(eq(projectCitations.id, data.citationId));

    return { success: true };
  });

/**
 * Get screening statistics for a project
 */
export const $getScreeningStats = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        // Exclude confirmed duplicates from stats
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      columns: {
        screeningStatus: true,
      },
    });

    const stats = {
      total: citations.length,
      pending: 0,
      included: 0,
      excluded: 0,
      maybe: 0,
    };

    for (const citation of citations) {
      const status = citation.screeningStatus ?? "pending";
      if (status in stats) {
        stats[status as keyof typeof stats]++;
      }
    }

    return stats;
  });

export interface ExclusionCode {
  id: string;
  code: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

/**
 * Get exclusion codes for a project
 * Lazily seeds default codes for existing projects that don't have any
 */
export const $getExclusionCodes = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Check if project has any exclusion codes
    let codes = await db.query.projectExclusionCodes.findMany({
      where: eq(projectExclusionCodes.projectId, data.projectId),
      orderBy: [asc(projectExclusionCodes.sortOrder)],
    });

    // If no codes exist, seed defaults (lazy migration for existing projects)
    if (codes.length === 0) {
      const newCodes = DEFAULT_EXCLUSION_CODES.map((code, index) => ({
        id: nanoid(),
        projectId: data.projectId,
        code: code.code,
        label: code.label,
        description: code.description,
        sortOrder: index,
        isActive: true,
      }));

      await db.insert(projectExclusionCodes).values(newCodes);

      codes = await db.query.projectExclusionCodes.findMany({
        where: eq(projectExclusionCodes.projectId, data.projectId),
        orderBy: [asc(projectExclusionCodes.sortOrder)],
      });
    }

    return codes.map((c) => ({
      id: c.id,
      code: c.code,
      label: c.label,
      description: c.description,
      sortOrder: c.sortOrder,
      isActive: c.isActive,
    })) satisfies ExclusionCode[];
  });
